import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import Icon from 'dpl/common/components/Icon';
import Toggle from 'dpl/common/design_system/Toggle';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import {
  RESULTS_TYPES,
  SEARCH_ALERTS_FEATURE_KEY
} from 'dpl/puppy_search_results/utils/constants';
import { FOREVER_DURATION, useSnoozed } from 'dpl/shared/hooks/useSnoozed';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import {
  FILTER_LOCATION_NAME,
  FILTER_POSTAL_CODE
} from 'dpl/constants/query_params';
import useScrollTrigger from 'dpl/pet_insurance/hooks/useScrollTrigger';

import AlertsMobileMenu from './AlertsMobileMenu';
import AlertsModal from './AlertsModal';

export const ALERTS_MODAL = {
  SIGN_UP: 'sign_up',
  SUCCESS: 'success'
};

const IS_LT_LG = isLessThanBreakpoint('lg');

export default function SearchAlertsToggle({ resultsType }) {
  const { isSnoozed, setSnoozed } = useSnoozed();
  const scrollTrigger = useScrollTrigger();
  const [elevated, setElevated] = useState(false);

  // Only show the toggle in mobile once we scroll past the header
  function handleScroll() {
    setElevated(
      !scrollTrigger &&
        window.scrollY > 188 &&
        window.scrollY < document.body.scrollHeight - 1040
    );
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const {
    emailListJoinedAt,
    breedId,
    breedNamePlural,
    locationTitle,
    searchAlertsDismissed,
    preappliedFilters: { cityName } = {}
  } = useSelector(({ server }) => server);

  const params = useSelector(({ queryParams }) => queryParams);

  const locationFilter = params[FILTER_LOCATION_NAME];
  const zipCodeFilter = params[FILTER_POSTAL_CODE];

  const alertsLocation =
    locationFilter || locationTitle || cityName || zipCodeFilter;

  const subtitle = alertsLocation
    ? `${breedNamePlural} in ${alertsLocation}`
    : `${breedNamePlural} near you`;

  const [toggleValue, setToggleValue] = useState(false);
  const [openAlertsModal, setOpenAlertsModal] = useState(null);

  const isToggleHidden =
    searchAlertsDismissed ||
    isSnoozed(`${SEARCH_ALERTS_FEATURE_KEY}-${breedId}`, FOREVER_DURATION) ||
    emailListJoinedAt;

  const analyticsView =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
      : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

  function handleToggleChange() {
    setOpenAlertsModal(ALERTS_MODAL.SIGN_UP);
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: 'get_notified_toggle',
      view: analyticsView,
      view_feature: ANALYTICS_VIEW_FEATURES.SEARCH_ALERTS_TOGGLE,
      view_feature_location: IS_LT_LG
        ? ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_FOOTER
        : ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_HEADER
    });
  }

  return (
    <>
      {!isToggleHidden && (
        <button
          className={classnames(
            'SearchAlertsToggle bg-white b--default ph4 ph3-md pv4 pv3-md nv2-md',
            {
              'bt transition z-999 bottom-0 sticky w-100': IS_LT_LG,
              dn: IS_LT_LG && !elevated,
              'ba br3 ml2': !IS_LT_LG
            }
          )}
          type="button"
          onClick={() => {
            setToggleValue(true);
            // In order to see the toggle value change before opening the bottom sheet
            setTimeout(handleToggleChange, 300);
          }}
        >
          <div className="flex justify-between w-100 items-center lh-normal">
            <div className="inline-flex items-center">
              <Icon
                className="padding-v-6 padding-h-6 bg-blue-500 white br2 mr2 mr3-md"
                name="fetch-bell"
                height="32px"
                width="32px"
              />
              <div className="mr2 mr3-md">
                <p
                  className={classnames('fw-medium font-14', {
                    'margin-b-2': !IS_LT_LG
                  })}
                >
                  Get alerts
                </p>
                <p
                  className={classnames('secondary', {
                    'font-13': IS_LT_LG,
                    'font-12': !IS_LT_LG
                  })}
                >
                  {subtitle}
                </p>
              </div>
            </div>
            <Toggle
              onClick={e => {
                // Prevent duplicate event triggers between toggle and parent element
                e.preventDefault();
                e.stopPropagation();
              }}
              onChange={e => {
                // Prevent duplicate event triggers between toggle and parent element
                e.preventDefault();
                e.stopPropagation();
              }}
              checked={toggleValue}
            />
          </div>
        </button>
      )}
      {IS_LT_LG && (
        <AlertsMobileMenu
          openAlertsModal={openAlertsModal}
          setToggleValue={setToggleValue}
          setOpenAlertsModal={value => setOpenAlertsModal(value)}
          analyticsView={analyticsView}
          setSnoozed={setSnoozed}
        />
      )}
      {!IS_LT_LG && Boolean(openAlertsModal) && (
        <AlertsModal
          openAlertsModal={openAlertsModal}
          setToggleValue={setToggleValue}
          setOpenAlertsModal={value => setOpenAlertsModal(value)}
          analyticsView={analyticsView}
          setSnoozed={setSnoozed}
        />
      )}
    </>
  );
}

SearchAlertsToggle.propTypes = {
  resultsType: PropTypes.string.isRequired
};
